import React, { useState } from 'react';
import { Redirect, useLocation } from 'react-router-dom';

import { acceptInvite } from '../api';
import type { TLink } from '../api';
import { SIGN_IN_PATHS } from '../constants';
import { initialsOf, joinAnd, passwordOk, passwordRules } from '../rules';
import { Actions, BigButton, Check, Field, Frame, InviterCard, LinkRow, Messages, Rules, TextLink, Title } from '../ui';
import { useLive, useOutcome } from '../useOutcome';
import { useLink } from './ResetScreen';

const InviteForm = ({ token, link }: { token: string; link: TLink }) => {
  const team = link.purpose === 'team-invite';
  const email = link.email || '';
  const inviter = link.inviterName || (team ? 'Your coach' : 'StrengthX');
  const inviterFirst = inviter.split(/\s+/)[0];
  const [firstName, setFirstName] = useState(link.firstName || '');
  const [lastName, setLastName] = useState(link.lastName || '');
  const [password, setPassword] = useState('');
  const [shown, setShown] = useState(false);
  const [terms, setTerms] = useState(false);
  const [error, setError] = useState('');
  const [busy, setBusy] = useState(false);
  const live = useLive();
  const outcome = useOutcome();

  const ready = !!firstName.trim() && !!lastName.trim() && passwordOk(password, email) && terms;

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!ready || busy) {
      return;
    }
    setBusy(true);
    setError('');
    const answer = await acceptInvite({ token, firstName: firstName.trim(), lastName: lastName.trim(), newPassword: password, acceptTerms: terms });
    const message = await outcome(answer, { keepSignedIn: true, email });
    if (live.current) {
      setBusy(false);
      if (message) {
        setError(message);
      }
    }
  };

  const areas = link.areas || [];
  const card = team
    ? { initials: initialsOf(inviter), title: `${inviter} invited you`, sub: `As ${link.role || 'Team member'}${areas.length ? ` · ${joinAnd(areas)}` : ''}` }
    : {
      initials: 'SX',
      title: 'StrengthX set up your account',
      sub: link.clientsLimit != null ? `Coach account · up to ${link.clientsLimit} active clients` : 'Coach account',
    };

  return (
    <Frame>
      <Title
        title={team ? `Join ${inviterFirst}’s team` : 'Welcome to StrengthX'}
        sub={team ? `Set up your account to help coach ${inviterFirst}’s clients.` : 'Set up your coach account. It takes a minute.'}
      />
      <InviterCard {...card} />
      <form onSubmit={submit} noValidate>
        <Field id="sx-first-name" label="First name" value={firstName} onChange={setFirstName} autoComplete="given-name" />
        <Field id="sx-last-name" label="Last name" value={lastName} onChange={setLastName} autoComplete="family-name" />
        <Field
          id="sx-email"
          label="Email"
          value={email}
          readOnly
          hint="This is the email you were invited with. You’ll sign in with it."
        />
        <Field
          id="sx-new-password"
          label="Password"
          value={password}
          onChange={setPassword}
          autoComplete="new-password"
          password={{ shown, onToggle: () => setShown((now) => !now) }}
        />
        <Rules rules={passwordRules(password, email)} />
        <Check checked={terms} onChange={setTerms} label="I agree to the StrengthX terms and privacy policy" />
        <Messages error={error} />
        <Actions>
          <BigButton type="submit" disabled={!ready} aria-busy={busy}>{team ? 'Join the team' : 'Create my account'}</BigButton>
        </Actions>
        <LinkRow>
          <TextLink to={SIGN_IN_PATHS.SIGN_IN}>Already have an account? Sign in</TextLink>
        </LinkRow>
      </form>
    </Frame>
  );
};

/** /sign-in/invite?token=...: a coach set up by StrengthX, or a team member invited by their head coach. */
export const InviteScreen = () => {
  const token = new URLSearchParams(useLocation().search).get('token');
  const link = useLink(token);

  if (!token) {
    return <Redirect to={SIGN_IN_PATHS.SIGN_IN} />;
  }
  if (!link) {
    return <Frame />;
  }
  if (!link.valid) {
    return <Redirect to={SIGN_IN_PATHS.EXPIRED} />;
  }
  if (link.purpose === 'reset') {
    return <Redirect to={`${SIGN_IN_PATHS.RESET}?token=${encodeURIComponent(token)}`} />;
  }
  return <InviteForm token={token} link={link} />;
};
